/**
 * Email Verification Page Component
 * Verifies user email via URL params (uidb64 + token) on mount
 */
import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, CheckCircle } from 'lucide-react'
import apiClient from '@/services/api/client'

export function VerifyEmailPage() {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [countdown, setCountdown] = useState(3)

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        await apiClient.get(`/api/v1/auth/verify/${uidb64}/${token}/`)
        setSuccess(true)
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { detail?: string } } }
        setError(
          axiosError.response?.data?.detail ||
          'Ошибка верификации. Ссылка недействительна или устарела.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    verifyEmail()
  }, [uidb64, token])

  useEffect(() => {
    if (!success) return
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          navigate('/login')
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [success, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-blue-600">
            SMETA AI
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Подтверждение email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Платформа автоматизации строительных смет
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {isLoading && (
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">Проверяем вашу ссылку...</p>
            </div>
          )}

          {success && (
            <div className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <p className="text-center text-lg font-medium text-gray-900">
                  Email подтверждён! Теперь вы можете войти.
                </p>
              </div>
              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Перейти на страницу входа ({countdown})
                </Link>
              </div>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <p className="text-center text-sm text-gray-600">
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Вернуться на страницу входа
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
